import { corsUrl, loadBitmap, type AssetCatalog } from './assets';
import { WING_SHOW_SCALE, wingAtlas } from './character-sheet';
import { WING_MOTION } from './wing-motion';

/** Quadros por segundo do bater de asas: os clipes do legado tocam na taxa do palco do jogo (25), como a explosão das bombas. */
export const WING_FPS = 25;

interface RawFrame {
  frame: { x: number; y: number; w: number; h: number };
  spriteSourceSize?: { x: number; y: number };
}

/**
 * Asas animadas do boneco em pé (`wing_show` do legado): desenha em uma tela (`canvas`) as animações de dentro do símbolo, cada uma em loop, com a
 * matriz em que o símbolo as coloca (a asa direita é a esquerda espelhada). A tela é a união de todos os quadros, na escala do corpo (`WING_SHOW_SCALE`).
 * `x` e `y` dizem onde o canto da tela cai no quadro do corpo de 250 px, a (-1, 2) da origem do boneco (`ShowCharacter.updateWing`).
 */
export class WingShowPlayer {
  readonly canvas = document.createElement('canvas');
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;

  private constructor(
    private readonly pic: string,
    private readonly sheet: ImageBitmap,
    private readonly frames: Record<string, RawFrame>,
  ) {
    const motion = WING_MOTION[pic]!.s!;
    this.x = (-1 + motion.t[0]) * WING_SHOW_SCALE;
    this.y = (2 + motion.t[1]) * WING_SHOW_SCALE;
    this.width = motion.z[0] * WING_SHOW_SCALE;
    this.height = motion.z[1] * WING_SHOW_SCALE;
    this.canvas.width = Math.ceil(this.width);
    this.canvas.height = Math.ceil(this.height);
  }

  /** A arte baixada de cada asa, compartilhada: cada boneco cria a própria tela, mas a folha e os quadros são os mesmos. */
  private static readonly art = new Map<string, Promise<{ sheet: ImageBitmap; frames: Record<string, RawFrame> } | null>>();

  /** `null` se a asa não tem animação conhecida, arte no manifest ou se a arte não baixou. Cada chamada devolve um player novo (uma tela própria). */
  static async load(assets: AssetCatalog, pic: string): Promise<WingShowPlayer | null> {
    if (!WING_MOTION[pic]?.s) return null;
    let art = WingShowPlayer.art.get(pic);
    if (!art) {
      art = WingShowPlayer.download(assets, pic).catch(() => null);
      WingShowPlayer.art.set(pic, art);
    }
    const loaded = await art;
    return loaded ? new WingShowPlayer(pic, loaded.sheet, loaded.frames) : null;
  }

  private static async download(assets: AssetCatalog, pic: string): Promise<{ sheet: ImageBitmap; frames: Record<string, RawFrame> } | null> {
    const json = wingAtlas(pic);
    const webp = json.replace(/\.json$/, '.webp');
    if (!assets.has(json) || !assets.has(webp)) return null;
    const response = await fetch(corsUrl(assets.url(json)));
    if (!response.ok) return null;
    const atlas = (await response.json()) as { frames: Record<string, RawFrame> };
    const sheet = await loadBitmap(assets.url(webp));
    return sheet ? { sheet, frames: atlas.frames } : null;
  }

  /** Desenha o quadro `tick` da animação (conta de 0, em loop). */
  draw(tick: number): void {
    const ctx = this.canvas.getContext('2d');
    const motion = WING_MOTION[this.pic]!.s!;
    if (!ctx) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    for (const sprite of motion.p) {
      const f = this.frames[`${sprite.n}/${(tick % sprite.f) + 1}`];
      if (!f) continue;
      // A origem da tela é o canto da união; a escala do corpo vale para tudo.
      ctx.setTransform(WING_SHOW_SCALE, 0, 0, WING_SHOW_SCALE, -motion.t[0] * WING_SHOW_SCALE, -motion.t[1] * WING_SHOW_SCALE);
      ctx.transform(sprite.m[0], sprite.m[1], sprite.m[2], sprite.m[3], sprite.m[4], sprite.m[5]);
      const off = f.spriteSourceSize ?? { x: 0, y: 0 };
      ctx.drawImage(this.sheet, f.frame.x, f.frame.y, f.frame.w, f.frame.h, sprite.t[0] + off.x, sprite.t[1] + off.y, f.frame.w, f.frame.h);
    }
  }
}
